import { ReactElement } from "react";
import StatusChartWrapper, { StatusChartWrapperConfig } from "./StatusChartWrapper";
import Chart from "../../Components/Charts/Base/Chart";
import {
  GetVisibleRangeSingleSeries,
  GetVisibleRangePressureSeries,
  GetVisibleRangeMultipleSeries,
  GetVisibleRangeDiagnosticSeries,
  GetVisibleRangeComplianceSeries,
  GetVisibleRangeTimelineSeries
} from "../../../../Utilities/ChartUtilities";

export type ChartType = 
  | 'activity'
  | 'activity-pressure'
  | 'activity-flow-rate'
  | 'asset'
  | 'asset-pressure'
  | 'asset-flow-rate'
  | 'diagnostics'
  | 'compliance'
  | 'timeline';

interface BaseProps {
  statusChartData: any;
  isSimpleChart?: boolean;
  chartParentHeight?: number;
  showLegendOverride?: boolean;
  greasePumpData?: {
    serialNo: string;
    channelNo: number;
    timestamps: number[];
    values: number[];
  };
  // Chart-specific props
  visibleDataSeries?: any;
  isFullscreen?: boolean;
  addHitTestRadius?: boolean;
  isLegendColumn?: boolean;
  showStatus?: boolean;
}

interface ActivityProps extends BaseProps {
  chartType: 'activity';
  activityChartData: any;
}

interface ActivityPressureProps extends BaseProps {
  chartType: 'activity-pressure';
  activityPressureChartData: any;
}

interface ActivityFlowRateProps extends BaseProps {
  chartType: 'activity-flow-rate';
  activityFlowRateChartData: any;
}

interface AssetProps extends BaseProps {
  chartType: 'asset';
  activityDataChart: any;
  legendConfig?: { [key: string]: boolean };
}

interface AssetPressureProps extends BaseProps {
  chartType: 'asset-pressure';
  activityDataChart: any;
  visibleDataSeries: any;
}

interface AssetFlowRateProps extends BaseProps {
  chartType: 'asset-flow-rate';
  activityDataChart: any;
  visibleDataSeries: {
    volume?: boolean;
    volumeTemperatureCompensated?: boolean;
    flowRate?: boolean;
    flowRateTemperatureCompensated?: boolean;
    temperature?: boolean;
  };
  isFullscreen?: boolean;
}

interface DiagnosticsProps extends BaseProps {
  chartType: 'diagnostics';
  diagnosticsDataChart: any;
  isSimpleChart: boolean; // Required for diagnostics
}

interface ComplianceProps extends BaseProps {
  chartType: 'compliance';
  complianceDataChart: any;
  isSimpleChart: boolean; // Required for compliance
}

interface TimelineProps extends BaseProps {
  chartType: 'timeline';
  timelineData: any;
  isSimpleChart: boolean; // Required for timeline
}

type Props = 
  | ActivityProps 
  | ActivityPressureProps 
  | ActivityFlowRateProps 
  | AssetProps 
  | AssetPressureProps 
  | AssetFlowRateProps 
  | DiagnosticsProps 
  | ComplianceProps 
  | TimelineProps;

export default function EndpointStatusChart(props: Props): ReactElement {
  const getChartConfig = (): StatusChartWrapperConfig => {
    switch (props.chartType) {
      case 'activity': {
        const p = props as ActivityProps;
        return {
          chartComponent: Chart,
          chartComponentProps: { chartType: 'activity' as const },
          statusChartData: p.statusChartData,
          chartData: p.activityChartData,
          isSimpleChart: p.isSimpleChart,
          chartParentHeight: p.chartParentHeight,
          greasePumpData: p.greasePumpData,
          modifierIds: {
            modifierGroup: "ActivityModifierGroup",
            rollOver: "ActivityRollOver"
          },
          calculateVisibleRange: (visibleRangeRef, chartData, startDateUnix, endDateUnix) => {
            return GetVisibleRangeSingleSeries(
              visibleRangeRef,
              chartData.timestamps,
              startDateUnix,
              endDateUnix
            );
          },
          legendIds: {
            chart: "legend-activity-flow",
            status: "legend-status"
          },
          chartKey: "activity-chart",
          showLegendOverride: p.showLegendOverride,
          showStatus: p.showStatus
        };
      }

      case 'activity-pressure': {
        const p = props as ActivityPressureProps;
        return {
          chartComponent: Chart,
          chartComponentProps: { chartType: 'activity-pressure' as const },
          statusChartData: p.statusChartData,
          chartData: p.activityPressureChartData,
          isSimpleChart: p.isSimpleChart,
          chartParentHeight: p.chartParentHeight,
          greasePumpData: p.greasePumpData,
          modifierIds: {
            modifierGroup: "ActivityModifierGroup",
            rollOver: "ActivityRollOver"
          },
          calculateVisibleRange: (visibleRangeRef, chartData, startDateUnix, endDateUnix) => {
            const latestPressureTimestamp = chartData.pressureEvents?.timestamps?.[chartData.pressureEvents.timestamps.length - 1] || 0;
            const latestVolumeTimestamp = chartData.timestamps?.[chartData.timestamps.length - 1] || 0;
            return GetVisibleRangePressureSeries(
              visibleRangeRef,
              latestPressureTimestamp,
              latestVolumeTimestamp,
              startDateUnix,
              endDateUnix
            );
          },
          legendIds: {
            chart: "legend-activity-flow-pressure",
            status: "legend-status"
          },
          chartKey: "activity-pressure-chart",
          showLegendOverride: p.showLegendOverride,
          showStatus: p.showStatus
        };
      }

      case 'activity-flow-rate': {
        const p = props as ActivityFlowRateProps;
        return {
          chartComponent: Chart,
          chartComponentProps: { chartType: 'activity-flow-rate' as const, isFullscreen: p.isFullscreen },
          statusChartData: p.statusChartData,
          chartData: p.activityFlowRateChartData,
          isSimpleChart: p.isSimpleChart,
          chartParentHeight: p.chartParentHeight,
          greasePumpData: p.greasePumpData,
          isFullscreen: p.isFullscreen,
          modifierIds: {
            modifierGroup: "ActivityModifierGroup",
            rollOver: "ActivityRollOver"
          },
          calculateVisibleRange: (visibleRangeRef, chartData, startDateUnix, endDateUnix) => {
            return GetVisibleRangeSingleSeries(
              visibleRangeRef,
              chartData.timestamps,
              startDateUnix,
              endDateUnix
            );
          },
          legendIds: {
            chart: p.isFullscreen ? "legend-activity-flowRate-fullscreen" : "legend-activity-flowRate",
            status: p.isFullscreen ? "legend-status-fullscreen" : "legend-status"
          },
          chartKey: "activity-flowRate-chart",
          showLegendOverride: p.showLegendOverride,
          showStatus: p.showStatus
        };
      }

      case 'asset': {
        const p = props as AssetProps;
        return {
          chartComponent: Chart,
          chartComponentProps: { chartType: 'activity-asset' as const },
          statusChartData: p.statusChartData,
          chartData: p.activityDataChart,
          chartParentHeight: p.chartParentHeight,
          modifierIds: {
            modifierGroup: "AssetModifierGroup",
            rollOver: "AssetRollOver"
          },
          calculateVisibleRange: (visibleRangeRef, chartData, startDateUnix, endDateUnix) => {
            return GetVisibleRangeMultipleSeries(
              visibleRangeRef,
              chartData.dataChart,
              startDateUnix,
              endDateUnix
            );
          },
          legendIds: {
            chart: "legend-asset",
            status: "legend-status"
          },
          chartKey: "asset-chart",
          showLegendOverride: p.showLegendOverride
        };
      }

      case 'asset-pressure': {
        const p = props as AssetPressureProps;
        return {
          chartComponent: Chart,
          chartComponentProps: { 
            chartType: 'activity-asset-pressure' as const,
            visibleDataSeries: p.visibleDataSeries,
            isFullscreen: p.isFullscreen
          },
          statusChartData: p.statusChartData,
          chartData: p.activityDataChart,
          chartParentHeight: p.chartParentHeight,
          modifierIds: {
            modifierGroup: "AssetModifierGroup",
            rollOver: "AssetRollOver"
          },
          calculateVisibleRange: (visibleRangeRef, chartData, startDateUnix, endDateUnix) => {
            return GetVisibleRangeMultipleSeries(
              visibleRangeRef,
              chartData.dataChart,
              startDateUnix,
              endDateUnix
            );
          },
          legendIds: {
            chart: p.isFullscreen ? "legend-asset-fullscreen" : "legend-asset",
            status: p.isFullscreen ? "legend-status-fullscreen" : "legend-status"
          },
          chartKey: `asset-chart-${p.isFullscreen ? 'fullscreen' : 'regular'}`,
          sciChartGroupKey: `asset-chart-${p.isFullscreen ? 'fullscreen' : 'regular'}-group`,
          showLegendOverride: p.showLegendOverride
        };
      }

      case 'asset-flow-rate': {
        const p = props as AssetFlowRateProps;
        return {
          chartComponent: Chart,
          chartComponentProps: { 
            chartType: 'activity-asset-flow-rate' as const,
            visibleDataSeries: p.visibleDataSeries,
            isFullscreen: p.isFullscreen
          },
          statusChartData: p.statusChartData,
          chartData: p.activityDataChart,
          chartParentHeight: p.chartParentHeight,
          modifierIds: {
            modifierGroup: "AssetModifierGroup",
            rollOver: "AssetRollOver"
          },
          calculateVisibleRange: (visibleRangeRef, chartData, startDateUnix, endDateUnix) => {
            return GetVisibleRangeMultipleSeries(
              visibleRangeRef,
              chartData.dataChart,
              startDateUnix,
              endDateUnix
            );
          },
          legendIds: {
            chart: p.isFullscreen ? "legend-asset-fullscreen" : "legend-asset",
            status: p.isFullscreen ? "legend-status-fullscreen" : "legend-status"
          },
          chartKey: `asset-flow-rate-chart-${p.isFullscreen ? 'fullscreen' : 'regular'}`,
          sciChartGroupKey: `asset-flow-rate-chart-${p.isFullscreen ? 'fullscreen' : 'regular'}-group`,
          showLegendOverride: p.showLegendOverride
        };
      }

      case 'diagnostics': {
        const p = props as DiagnosticsProps;
        return {
          chartComponent: Chart,
          chartComponentProps: { 
            chartType: 'diagnostics' as const,
            addHitTestRadius: p.addHitTestRadius
          },
          statusChartData: p.statusChartData,
          chartData: p.diagnosticsDataChart,
          isSimpleChart: p.isSimpleChart,
          chartParentHeight: p.chartParentHeight,
          greasePumpData: p.greasePumpData,
          modifierIds: {
            modifierGroup: "DiagnosticsModifierGroup",
            rollOver: "DiagnosticsRollOver"
          },
          calculateVisibleRange: (visibleRangeRef, chartData, startDateUnix, endDateUnix) => {
            const latestDiagnosticTimestamp = chartData.timestampsDiagnostics?.length > 0 
              ? chartData.timestampsDiagnostics[chartData.timestampsDiagnostics.length - 1] 
              : 0;
            const latestMetricTimestamp = chartData.timestampsMetrics?.length > 0 
              ? chartData.timestampsMetrics[chartData.timestampsMetrics.length - 1] 
              : 0;
            const lastDataPointTimestamp = chartData.timestamps?.length > 0 
              ? chartData.timestamps[chartData.timestamps.length - 1] 
              : 0;
            
            return GetVisibleRangeDiagnosticSeries(
              visibleRangeRef,
              latestDiagnosticTimestamp,
              latestMetricTimestamp,
              lastDataPointTimestamp,
              startDateUnix,
              endDateUnix
            );
          },
          legendIds: {
            chart: "legend-diagnostics",
            status: "legend-status"
          },
          chartKey: "diagnostics-chart",
          isLegendColumn: p.isLegendColumn,
          showLegendOverride: p.showLegendOverride,
          showStatus: p.showStatus
        };
      }

      case 'compliance': {
        const p = props as ComplianceProps;
        return {
          chartComponent: Chart,
          chartComponentProps: { chartType: 'compliance' as const },
          statusChartData: p.statusChartData,
          chartData: p.complianceDataChart,
          isSimpleChart: p.isSimpleChart,
          chartParentHeight: p.chartParentHeight,
          greasePumpData: p.greasePumpData,
          modifierIds: {
            modifierGroup: "ComplianceModifierGroup",
            rollOver: "ComplianceRollOver"
          },
          calculateVisibleRange: (visibleRangeRef, chartData, startDateUnix, endDateUnix) => {
            const latestVolumeTimestamp = chartData.requirements?.timestamps?.length > 0 
              ? chartData.requirements.timestamps[chartData.requirements.timestamps.length - 1] 
              : 0;
            const latestUnplannedTimestamp = chartData.requirements?.unplannedEventsTimestamps?.length > 0 
              ? chartData.requirements.unplannedEventsTimestamps[chartData.requirements.unplannedEventsTimestamps.length - 1] 
              : 0;
            
            return GetVisibleRangeComplianceSeries(
              visibleRangeRef,
              latestVolumeTimestamp,
              latestUnplannedTimestamp,
              startDateUnix,
              endDateUnix
            );
          },
          legendIds: {
            chart: "legend-compliance",
            status: "legend-status"
          },
          chartKey: "compliance-chart",
          showLegendOverride: p.showLegendOverride,
          showStatus: p.showStatus
        };
      }

      case 'timeline': {
        const p = props as TimelineProps;
        // Create dynamic series data structure for timeline visible range calculation
        const timelineSeriesData: Array<{ timestamps: number[] }> = [];
        
        if (p.timelineData.seriesData && Array.isArray(p.timelineData.seriesData)) {
          p.timelineData.seriesData.forEach((series: any) => {
            if (series.timestamps && Array.isArray(series.timestamps)) {
              timelineSeriesData.push({ timestamps: series.timestamps });
            }
          });
        }

        return {
          chartComponent: Chart,
          chartComponentProps: { chartType: 'timeline' as const },
          statusChartData: p.statusChartData,
          chartData: p.timelineData,
          isSimpleChart: p.isSimpleChart,
          chartParentHeight: p.chartParentHeight,
          modifierIds: {
            modifierGroup: "TimelineModifierGroup",
            rollOver: "TimelineRollOver"
          },
          calculateVisibleRange: (visibleRangeRef, chartData, startDateUnix, endDateUnix) => {
            return GetVisibleRangeTimelineSeries(
              visibleRangeRef,
              timelineSeriesData,
              startDateUnix,
              endDateUnix
            );
          },
          legendIds: {
            chart: "legend-timeline",
            status: "legend-status"
          },
          chartKey: "timeline-chart",
          showStatus: p.showStatus,
          isLegendColumn: p.isLegendColumn,
          showLegendOverride: p.showLegendOverride
        };
      }
    }
  };

  return StatusChartWrapper(getChartConfig());
}
